/**
 * CrdtProvider — verbindt een lokale Y.Doc met de BranchDocument-DO via
 * WebSocket. Doet de 1-op-1 vertaling van het simpele wire-protocol uit
 * `apps/worker/src/do/BranchDocument.ts`:
 *
 *   Type 0x00 (initial sync, server→client):
 *     payload = volledige `Y.encodeStateAsUpdate` van de server-doc.
 *     We applyen 'm op de lokale doc; resulterende update-events met
 *     origin = "remote" lopen niet terug over de wire.
 *
 *   Type 0x01 (incremental update, beide kanten):
 *     Lokale Y-update events met origin ≠ "remote" worden over de wire
 *     gestuurd; binnenkomende updates worden geapplyd als origin
 *     "remote".
 *
 * **Reconnect-strategie:** exponential backoff capped op 30s. Bij elke
 * reconnect stuurt de server opnieuw een MSG_SYNC_INITIAL die de doc
 * weer aligned, dus we zijn na een netwerkstoring vanzelf consistent.
 *
 * **Lifecycle:** caller (de `useWorkspaceData` store) maakt één
 * provider per (workspace, branch). Bij branch-switch wordt de oude
 * `destroy()`-d en een nieuwe gestart.
 */
import { applyDocUpdate, encodeDocUpdate } from "@ae/crdt";
import type * as Y from "yjs";

const MSG_SYNC_INITIAL = 0x00;
const MSG_UPDATE = 0x01;
const MSG_AWARENESS = 0x02;
const MSG_HELLO = 0x03;
const MSG_POKE = 0x04;
/** Chat tussen co-editors (M4B). JSON-payload, zoals MSG_AWARENESS. */
const MSG_CHAT = 0x05;

/** Eén regel uit de awareness-snapshot. Spiegel van de DO-shape. */
export interface AwarenessRecord {
  /** Uniek per WS-verbinding; gebruikt om "mij" uit te filteren in de UI. */
  connectionId: string;
  user: string;
  state: Record<string, unknown>;
  updatedAt: number;
}

/** Origin-tag voor remote-incoming updates zodat onze update-handler
 *  ze niet terug-broadcast. */
export interface ChatMessage {
  id: string;
  senderEmail: string;
  recipientEmail: string;
  text: string;
  diagramRef?: { id: string; name: string };
  sentAt: number;
}

/** Wat de DO over de chat-byte terugstuurt. */
export type ChatWireEvent =
  | { op: "received"; message: ChatMessage }
  | { op: "sent"; message: ChatMessage }
  | { op: "read-receipt"; byEmail: string };

export const REMOTE_ORIGIN = Symbol("ae-crdt-remote");

const RECONNECT_INITIAL_MS = 500;
const RECONNECT_MAX_MS = 30_000;

export interface CrdtProviderOptions {
  workspaceId: string;
  branchId: string;
  doc: Y.Doc;
  /** Override de WS-URL; default = `ws(s)://<host>/api/v1/ws/...`. */
  url?: string;
  /** Roept aan zodra de eerste sync ontvangen is. */
  onSynced?: () => void;
  /** Roept aan bij verbindings-state-veranderingen (UI-indicator). */
  onStatusChange?: (status: CrdtConnectionStatus) => void;
  /** Roept aan bij elke incoming awareness-snapshot (peers + selfecho). */
  onAwarenessChange?: (records: AwarenessRecord[]) => void;
  /** Inkomend chat-verkeer: een bericht, een echo van je eigen bericht,
   *  of een leesbevestiging. */
  onChat?: (event: ChatWireEvent) => void;
  /** Roept aan zodra de server bevestigt welke `connectionId` we hebben. */
  onHello?: (info: { connectionId: string; user: string }) => void;
  /** Roept aan bij een POKE — een peer signaleerde dat hij D1 heeft
   *  gemuteerd en wij moeten rehydraten. */
  onPoke?: () => void;
}

export type CrdtConnectionStatus = "idle" | "connecting" | "connected" | "syncing" | "offline";

export class CrdtProvider {
  private doc: Y.Doc;
  private socket: WebSocket | null = null;
  private destroyed = false;
  private reconnectDelay = RECONNECT_INITIAL_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private status: CrdtConnectionStatus = "idle";
  private synced = false;
  private onSynced: (() => void) | undefined;
  private onStatusChange: ((status: CrdtConnectionStatus) => void) | undefined;
  private onAwarenessChange: ((records: AwarenessRecord[]) => void) | undefined;
  private onChat: ((event: ChatWireEvent) => void) | undefined;
  private onHello: ((info: { connectionId: string; user: string }) => void) | undefined;
  private onPoke: (() => void) | undefined;
  /** Listener-handle zodat we 'm op destroy weer kunnen verwijderen. */
  private docUpdateHandler: (update: Uint8Array, origin: unknown) => void;

  /** Workspace + branch zijn nodig om bij elke reconnect een fresh
   *  ticket op te halen (in productie). Lokaal ongebruikt. */
  private workspaceId: string;
  private branchId: string;
  /** Override-URL die de constructor optioneel meekrijgt. Als gezet,
   *  wordt deze hergebruikt bij elke reconnect — geen ticket-flow. */
  private urlOverride: string | undefined;

  constructor(opts: CrdtProviderOptions) {
    this.doc = opts.doc;
    this.workspaceId = opts.workspaceId;
    this.branchId = opts.branchId;
    this.urlOverride = opts.url;
    // `url` wordt lazy bepaald per reconnect zodat we elke keer een
    // fresh ticket halen (single-use). Voor lokaal/dev wordt 'm direct
    // gebouwd zonder ticket — same-origin via Vite-proxy.
    this.onSynced = opts.onSynced;
    this.onStatusChange = opts.onStatusChange;
    this.onAwarenessChange = opts.onAwarenessChange;
    this.onChat = opts.onChat;
    this.onHello = opts.onHello;
    this.onPoke = opts.onPoke;

    this.docUpdateHandler = (update, origin) => {
      // Ignore updates we just applied from the remote — anders
      // krijgen we een echo-loop.
      if (origin === REMOTE_ORIGIN) return;
      this.sendUpdate(update);
    };
    this.doc.on("update", this.docUpdateHandler);

    void this.connect();
  }

  destroy(): void {
    this.destroyed = true;
    this.doc.off("update", this.docUpdateHandler);
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close(1000, "destroy");
      } catch {
        // ignore
      }
      this.socket = null;
    }
    this.setStatus("idle");
  }

  /** True als we ten minste één sync-init hebben ontvangen. */
  isSynced(): boolean {
    return this.synced;
  }

  private setStatus(status: CrdtConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.onStatusChange?.(status);
  }

  private async connect(): Promise<void> {
    if (this.destroyed) return;
    this.setStatus("connecting");

    // URL bepalen — in productie betekent dat een fresh ticket halen
    // via /api/v1/ws/ticket. Bij elke reconnect opnieuw, want
    // tickets zijn single-use.
    let url: string;
    try {
      url = this.urlOverride ?? (await buildDefaultUrl(this.workspaceId, this.branchId));
    } catch (err) {
      console.error("[CrdtProvider] URL build failed", err);
      this.scheduleReconnect();
      return;
    }
    if (this.destroyed) return;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (err) {
      console.error("[CrdtProvider] WS construct failed", err);
      this.scheduleReconnect();
      return;
    }
    socket.binaryType = "arraybuffer";
    this.socket = socket;

    socket.addEventListener("open", () => {
      // Reset backoff zodra we een werkende verbinding hadden.
      this.reconnectDelay = RECONNECT_INITIAL_MS;
      this.setStatus("syncing");
    });

    socket.addEventListener("message", (event) => {
      const data = event.data;
      if (!(data instanceof ArrayBuffer)) return;
      const buf = new Uint8Array(data);
      if (buf.byteLength < 1) return;
      const type = buf[0];
      const payload = buf.slice(1);

      if (type === MSG_HELLO) {
        try {
          const text = new TextDecoder().decode(payload);
          const info = JSON.parse(text) as { connectionId: string; user: string };
          this.onHello?.(info);
        } catch (err) {
          console.error("[CrdtProvider] hello parse failed", err);
        }
      } else if (type === MSG_SYNC_INITIAL) {
        applyDocUpdate(this.doc, payload, REMOTE_ORIGIN);
        if (!this.synced) {
          this.synced = true;
          this.onSynced?.();
        }
        this.setStatus("connected");
      } else if (type === MSG_UPDATE) {
        applyDocUpdate(this.doc, payload, REMOTE_ORIGIN);
      } else if (type === MSG_POKE) {
        this.onPoke?.();
      } else if (type === MSG_CHAT) {
        try {
          this.onChat?.(JSON.parse(new TextDecoder().decode(payload)) as ChatWireEvent);
        } catch (err) {
          console.error("[CrdtProvider] chat parse failed", err);
        }
      } else if (type === MSG_AWARENESS) {
        try {
          const text = new TextDecoder().decode(payload);
          const records = JSON.parse(text) as AwarenessRecord[];
          if (Array.isArray(records)) {
            this.onAwarenessChange?.(records);
          }
        } catch (err) {
          console.error("[CrdtProvider] awareness parse failed", err);
        }
      }
    });

    socket.addEventListener("close", () => {
      this.socket = null;
      if (!this.destroyed) {
        this.setStatus("offline");
        this.scheduleReconnect();
      }
    });

    socket.addEventListener("error", (event) => {
      console.error("[CrdtProvider] WS error", event);
      // close-event zal automatisch volgen.
    });
  }

  private scheduleReconnect(): void {
    if (this.destroyed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS);
  }

  private sendUpdate(update: Uint8Array): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const message = packMessage(MSG_UPDATE, update);
    try {
      socket.send(message);
    } catch (err) {
      console.error("[CrdtProvider] send failed", err);
    }
  }

  /** Stuur een POKE — peers krijgen dit als signaal "rehydrate je
   *  store, want ik heb iets in D1 gemuteerd". Niet meer gebruikt
   *  door de standaard-flow (vervangen door `tickAfterMutation` in
   *  session.ts), maar blijft beschikbaar voor expliciete-poke
   *  scenario's of toekomstig debugging. */
  poke(): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      const ts = new Uint8Array(4);
      new DataView(ts.buffer).setUint32(0, Date.now() & 0xff_ff_ff_ff, false);
      socket.send(packMessage(MSG_POKE, ts));
    } catch (err) {
      console.error("[CrdtProvider] poke send failed", err);
    }
  }

  /** Push de eigen awareness-state naar de server. Wordt door peers
   *  ontvangen als één entry in de awareness-snapshot. */
  setAwarenessState(state: Record<string, unknown>): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const payload = new TextEncoder().encode(JSON.stringify({ state }));
    try {
      socket.send(packMessage(MSG_AWARENESS, payload));
    } catch (err) {
      console.error("[CrdtProvider] awareness send failed", err);
    }
  }

  /** Verstuur een bericht aan één peer in deze branch. De afzender wordt
   *  serverkant bepaald, dus die sturen we bewust niet mee. */
  sendChat(recipientEmail: string, text: string, diagramRef?: { id: string; name: string }): void {
    this.sendChatFrame({ op: "send", recipientEmail, text, ...(diagramRef ? { diagramRef } : {}) });
  }

  /** Meld dat we het gesprek met deze peer hebben geopend, zodat z'n
   *  envelopje verdwijnt. */
  sendChatOpened(peerEmail: string): void {
    this.sendChatFrame({ op: "opened", peerEmail });
  }

  private sendChatFrame(frame: Record<string, unknown>): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(packMessage(MSG_CHAT, new TextEncoder().encode(JSON.stringify(frame))));
    } catch (err) {
      console.error("[CrdtProvider] chat send failed", err);
    }
  }

  /** Voor tests / introspectie. */
  getStatus(): CrdtConnectionStatus {
    return this.status;
  }
}

function packMessage(type: number, payload: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(1 + payload.byteLength);
  out[0] = type;
  out.set(payload, 1);
  return out;
}

/**
 * Bouw de WS-URL op basis van environment.
 *
 * **Lokaal** (`localhost`): same-origin via Vite-proxy. Vite
 * upgrade WS keurig door naar de wrangler-dev-worker. Geen ticket
 * nodig — cookie werkt cross-handlers.
 *
 * **Productie** (Netlify-host): direct naar de worker-domain. De
 * Netlify-rewrite-proxy strept upgrade-headers waardoor de WS niet
 * werkt via `/api/v1/ws/...`. Daarom connecten we rechtstreeks op
 * `wss://<worker>/api/v1/ws-upgrade/...?ticket=<x>`; welk adres dat is,
 * zegt de worker bij het ticket (`ws_origin`).
 */
async function buildDefaultUrl(workspaceId: string, branchId: string): Promise<string> {
  const isLocalhost = /^(localhost|127\.|::1|\[::1\])/.test(location.host);
  const proto = location.protocol === "https:" ? "wss" : "ws";

  if (isLocalhost) {
    return `${proto}://${location.host}/api/v1/ws/workspaces/${encodeURIComponent(workspaceId)}/branches/${encodeURIComponent(branchId)}`;
  }

  // Productie: ticket halen via Netlify-proxy (cookie werkt voor
  // gewone HTTP), daarna direct naar worker connecten.
  const ticketRes = await fetch("/api/v1/ws/ticket", {
    method: "POST",
    credentials: "include",
  });
  if (!ticketRes.ok) {
    throw new Error(`Failed to obtain WS ticket: HTTP ${ticketRes.status}`);
  }
  const { ticket, ws_origin } = (await ticketRes.json()) as {
    ticket: string;
    ws_origin?: string;
  };
  const origin = ws_origin ?? `${proto}://${location.host}`;
  return `${origin}/api/v1/ws-upgrade/workspaces/${encodeURIComponent(workspaceId)}/branches/${encodeURIComponent(branchId)}?ticket=${ticket}`;
}

/**
 * Encodeert de huidige doc-state als update-bytes — handig voor
 * debugging / inspectie van wat de provider zou versturen.
 */
export function encodeFull(doc: Y.Doc): Uint8Array {
  return encodeDocUpdate(doc);
}
